import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ChevronDown, Mail, Phone, Quote, Star, Facebook, Instagram, Youtube, Music, Menu, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Photo gallery state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showreelOpen, setShowreelOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const galleryImages = [
    { src: "https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/bz97w4k5_Screenshot%202026-05-09%20at%207.19.52%E2%80%AFPM.png", alt: "Ryan Daniels performing with shocked audience" },
    { src: "https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/n04ygak3_Screenshot%202026-05-09%20at%207.18.11%E2%80%AFPM.png", alt: "Ryan Daniels performing at a holiday event" },
    { src: "https://customer-assets.emergentagent.com/job_eb0f6d77-0887-4a47-858f-d49ce1e2b7c3/artifacts/k2ozu0wn_IMG_1511.JPG", alt: "Ryan Daniels performing magic" },
  ];
  const videoRef = React.useRef(null);
  const newsletterRef = useRef(null);

  // Initialize newsletter widget
  useEffect(() => {
    if (!newsletterRef.current) return;
    
    const API_URL = "https://dual-brand-email.emergent.host/api/subscribe";
    const LIST_ID = "c7f60b5c-7ea8-468c-b456-1787a181286e";
    
    const container = newsletterRef.current;
    
    // Add styles
    const styleId = 'rd-widget-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .rd-widget {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
          max-width: 600px;
          margin: 0 auto;
          padding: 48px 24px;
          background: transparent;
          text-align: center;
        }
        .rd-widget .rd-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          background: #FF1F8F;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .rd-widget .rd-icon svg {
          width: 40px;
          height: 40px;
          fill: none;
          stroke: #fff;
          stroke-width: 2;
        }
        .rd-widget h3 {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
          font-weight: 700;
          color: #FF1F8F;
          font-size: 3rem;
          letter-spacing: -0.025em;
          margin: 0 0 1.5rem 0;
          text-transform: uppercase;
        }
        @media (min-width: 768px) {
          .rd-widget h3 {
            font-size: 3.75rem;
          }
        }
        .rd-widget p {
          color: #d1d5db;
          font-size: 18px;
          margin: 0 0 32px 0;
          line-height: 1.6;
        }
        .rd-widget form {
          display: flex;
          justify-content: center;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 24px;
        }
        .rd-widget input {
          flex: 1;
          min-width: 250px;
          max-width: 350px;
          padding: 16px 24px;
          background: #1f1f1f;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 50px;
          color: #EDEDED;
          font-size: 16px;
        }
        .rd-widget input:focus {
          outline: none;
          border-color: #FF1F8F;
        }
        .rd-widget input::placeholder {
          color: #6b7280;
        }
        .rd-widget button {
          padding: 16px 40px;
          background: #FF1F8F;
          border: none;
          border-radius: 50px;
          color: #fff;
          font-weight: 600;
          font-size: 18px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          transition: opacity 0.2s;
          white-space: nowrap;
        }
        .rd-widget button:hover {
          opacity: 0.9;
        }
        .rd-widget button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .rd-widget .rd-disclaimer {
          color: #6b7280;
          font-size: 14px;
          margin: 0;
        }
        .rd-widget .rd-message {
          margin-top: 16px;
          padding: 12px 20px;
          border-radius: 50px;
          font-size: 14px;
        }
        .rd-widget .rd-success {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10B981;
        }
        .rd-widget .rd-error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #EF4444;
        }
        @media (max-width: 640px) {
          .rd-widget form {
            flex-direction: column;
            align-items: center;
          }
          .rd-widget input {
            width: 100%;
            max-width: 100%;
          }
          .rd-widget button {
            width: 100%;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    container.innerHTML = `
      <div class="rd-widget">
        <div class="rd-icon">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="2" y="4" width="20" height="16" rx="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3>Stay in the Loop</h3>
        <p>Be the first to know when new tour dates are announced. Sign up for the Newsletter!</p>
        <form id="rd-form">
          <input type="email" id="rd-email" placeholder="Enter your email" required />
          <button type="submit">Notify Me</button>
        </form>
        <p class="rd-disclaimer">No spam, just show announcements. Unsubscribe anytime.</p>
        <div id="rd-message"></div>
      </div>
    `;
    
    const form = document.getElementById('rd-form');
    const emailInput = document.getElementById('rd-email');
    const messageDiv = document.getElementById('rd-message');
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      const button = form.querySelector('button');
      const email = emailInput.value;
      
      button.disabled = true;
      button.textContent = 'Subscribing...';
      messageDiv.innerHTML = '';
      
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, list_id: LIST_ID })
        });
        
        const data = await response.json();
        
        if (response.ok) {
          messageDiv.innerHTML = '<div class="rd-message rd-success">' + data.message + '</div>';
          emailInput.value = '';
        } else {
          messageDiv.innerHTML = '<div class="rd-message rd-error">' + (data.detail || 'Something went wrong') + '</div>';
        }
      } catch (error) {
        messageDiv.innerHTML = '<div class="rd-message rd-error">Failed to subscribe. Please try again.</div>';
      }
      
      button.disabled = false;
      button.textContent = 'Notify Me';
    };
    
    form.addEventListener('submit', handleSubmit);
    
    return () => {
      form.removeEventListener('submit', handleSubmit);
    };
  }, []);

  // Preload all gallery images for smooth transitions
  useEffect(() => {
    galleryPhotos.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);
  
  // Placeholder images for gallery (10 photos)
  const galleryPhotos = [
    // Start with professional notepad portrait
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/c4xpr1eg_Notepad.jpeg',
    // Switch to live performance energy
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/69tdt1gm_07A5D8BE-7ED5-4EE7-BF88-219CF71DED43.jpeg',
    // Dramatic mentalism shot
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/6prjzu7l_Card%20to%20Head%20Black%20BKG.jpg',
    // Intimate venue performance
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/b4y01ujb_IMG_1817.JPG',
    // Promotional design
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/64v87ytj_Untitled%20design-17.png',
    // Group stage performance
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/pretivjh_IMG_0181.jpeg',
    // Live performance shot
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/vx2i583e_1O7A8719.jpg.webp',
    // Black & White Live Shows
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/ebf0prii_IMG_1816.JPG',
    // Performance shot
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/wh0we9od_IMG_1697.jpeg',
    // End with memorable Ghosted promo
    'https://customer-assets.emergentagent.com/job_magicwizard/artifacts/u0lnatyv_Ghosted%20Flowers.jpg'
  ];
  
  // Auto-cycle through photos every 5 seconds with smooth fade effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        
        // After fade out completes, change image and fade in
        setTimeout(() => {
          setCurrentPhotoIndex((prevIndex) => (prevIndex + 1) % galleryPhotos.length);
          // Small delay to ensure image src is updated before fading in
          setTimeout(() => {
            setIsTransitioning(false);
          }, 50);
        }, 500);
      }
    }, 4000);
    
    return () => clearInterval(interval);
  }, [galleryPhotos.length, isTransitioning]);

  // Handle manual photo selection
  const handlePhotoSelect = (index) => {
    if (index !== currentPhotoIndex && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPhotoIndex(index);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 300);
    }
  };
  
  // No auto-play observer - user must click to play video
  // This ensures instant thumbnail display with no loading delay

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use different offsets for showreel based on screen size
      let headerOffset = 60;
      if (sectionId === 'showreel') {
        // Larger offset to position the video lower on screen
        headerOffset = window.innerWidth < 768 ? 150 : 200;
      }
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setFormSubmitted(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send message. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Real testimonials from Google Reviews and GigSalad
  const testimonials = [
  {
    text: "Ryan was an absolute joy to work with from start to finish. Ryan's ability to captivate an audience made the evening a memorable one!",
    author: "Elise",
    event: "Mimslyn Inn",
    platform: "Direct",
    date: "July 2026",
    stars: 5
  },
  {
    text: "He's a great performer both in platform type shows and in closeup/strolling settings.",
    author: "Jobeth Bowers",
    event: "ElktonMagic Coordinator",
    platform: "Google",
    stars: 5
  },
  {
    text: "Because of him I had the event of the year!",
    author: "Bianca A.",
    event: "Event Performance",
    platform: "GigSalad",
    date: "October 2023",
    stars: 5
  },
  {
    text: "The crowd loved him. People still ask me at parties how he did certain tricks.",
    author: "Tammy Ruggiero",
    event: "Crone and Crow Show",
    platform: "Google",
    stars: 5
  },
  {
    text: "We had a blast with Ryan!!",
    author: "Hallie R.",
    event: "Event Performance",
    platform: "GigSalad",
    date: "July 2025",
    stars: 5
  },
  {
    text: "Ryan sets the bar high when it comes to comedic interactive performances.",
    author: "Jordan B.",
    event: "Event Performance",
    platform: "Google",
    stars: 5
  }];


  // Client logos placeholder data
  const clientLogos = [
  { name: "Corporate Events", initial: "CE" },
  { name: "Private Parties", initial: "PP" },
  { name: "Theater Shows", initial: "TS" },
  { name: "Wedding Events", initial: "WE" },
  { name: "Company Galas", initial: "CG" },
  { name: "Festivals", initial: "FE" }];


  return (
    <div className="min-h-screen text-white relative" style={{ background: 'linear-gradient(to bottom, #000000 0%, #0a0a0a 8%, #0f0f0f 15%, #0c0c0c 25%, #090909 40%, #060606 55%, #040404 70%, #020202 85%, #000000 100%)' }}>
      {/* Subtle pink glows */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-[10%] -left-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[35%] -right-64 w-[800px] h-[1200px] bg-magenta/[0.02] rounded-full blur-[200px]"></div>
        <div className="absolute top-[65%] -left-64 w-[700px] h-[1000px] bg-pink-500/[0.015] rounded-full blur-[200px]"></div>
      </div>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg border-b border-white/5 bg-black/80">
        <div className="max-w-7xl mx-auto px-6 py-0">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="cursor-pointer"
              aria-label="Return to top"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/mjuj40fa_Ryan%20daniels%20mentalist-4.png"
                alt="Ryan Daniels Mentalist"
                style={{ height: '140px', marginTop: '-8px', marginBottom: '-8px' }}
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('media')} className="nav-link">Experience</button>
              <button onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</button>
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('shows')} className="nav-link">Tour</button>
              <button onClick={() => scrollToSection('ghosted')} className="nav-link">Ghosted</button>
              <a href="/corporate" className="nav-link">Corporate</a>
              <button onClick={() => scrollToSection('contact')} className="cta-button">Book Now</button>
            </div>
            
            {/* Mobile Hamburger */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              style={{ color: 'var(--color-magenta)' }}
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-white/10">
            <div className="flex flex-col py-4 px-6 gap-4">
              <button onClick={() => scrollToSection('media')} className="nav-link text-left py-2">Experience</button>
              <button onClick={() => scrollToSection('testimonials')} className="nav-link text-left py-2">Testimonials</button>
              <button onClick={() => scrollToSection('about')} className="nav-link text-left py-2">About</button>
              <button onClick={() => scrollToSection('shows')} className="nav-link text-left py-2">Tour</button>
              <button onClick={() => scrollToSection('ghosted')} className="nav-link text-left py-2">Ghosted</button>
              <a href="/corporate" className="nav-link text-left py-2">Corporate</a>
              <button onClick={() => scrollToSection('contact')} className="cta-button w-full">Book Now</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 overflow-hidden pt-20 md:pt-0 md:min-h-screen md:flex md:items-center md:justify-center" style={{ background: 'transparent' }}>
        {/* Background image — mobile: image drives section height (natural), content overlays inside; desktop: image fills the min-h-screen section */}
        <div className="relative w-full md:absolute md:inset-0 md:z-0">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/1ax1ihxf_Screenshot%202026-07-19%20at%202.56.01%E2%80%AFPM.webp"
            alt="Ryan Daniels Performance"
            className="block w-[128%] h-auto max-w-none -ml-[28%] md:w-[120%] md:h-auto md:absolute md:right-0 md:top-0 md:ml-0"
          />
          {/* Vertical gradient overlay — fades to transparent at the bottom so the lower letterbox bar blends seamlessly into the page gradient */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.7) 90%, rgba(0,0,0,1) 100%)' }}></div>
          {/* Mobile content — absolutely positioned inside the image area */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4 md:hidden">
            <h1 className="hero-headline-mobile mb-2">Funny. Interactive. Slightly Concerning.</h1>
            <p className="hero-subheadline-mobile mb-3 mx-auto px-2">Impossible experiences for people who say they hate mentalists</p>
            <div className="flex items-center justify-center gap-2 flex-wrap mt-1">
              <button onClick={() => scrollToSection('contact')} className="hero-mini-btn hero-mini-btn-outline">Book Ryan</button>
              <button onClick={() => setShowreelOpen(true)} className="hero-mini-btn hero-mini-btn-solid">Watch showreel</button>
            </div>
          </div>
        </div>
        {/* Desktop content */}
        <div className="hidden md:block relative z-20 text-center px-6 max-w-6xl mx-auto">
          <h1 className="hero-headline mb-6">Funny. Interactive. Slightly Concerning.</h1>
          <p className="hero-subheadline mb-16 mx-auto px-2">Impossible experiences for people who say they hate mentalists</p>
          <div className="flex items-center justify-center gap-4 flex-wrap mt-8">
            <button onClick={() => scrollToSection('contact')} className="cta-button-outline large text-base">Book Ryan</button>
            <button onClick={() => setShowreelOpen(true)} className="cta-button large text-base">Watch showreel</button>
          </div>
        </div>
      </section>

      {/* Audience Statement Section */}
      <section className="pt-16 md:pt-24 pb-20 md:pb-28 px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #ff1f8f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>The Audience Becomes The Show</h2>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed">Every performance is built around real people, real reactions, and moments no one sees coming</p>
        </div>
      </section>

      {/* Client Logos Scrolling Banner - Featured Clients & Partners */}
      <section className="py-16 px-6 relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Trusted By</h2>
            <div className="section-divider"></div>
          </div>
          <div className="logo-scroll-container">
            <div className="logo-scroll">
              {/* First set of logos */}
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/9eac6892-84ea-4861-9538-4b88e9b7eb14/IMG_1068.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/4466ad3a-6f58-49dd-9917-1aee6231cb9e/IMG_1066.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/5c77a29c-924e-47e8-93d7-80cdba71bb80/IMG_1063.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/73809047-f1f5-4aec-b6d4-f29045a3bc16/IMG_1065.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/c4c02014-5957-4314-97b7-6b5a57cc61d7/IMG_1064.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/f17b089a-a2b5-4898-9b97-9f86f3759162/IMG_1067.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/0dc06ff2-f8be-497d-91ac-ebcee7239241/dc-magic-festival-2024-png.jpg"
                  alt="DC Magic Festival 2024"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/768acfd1-944d-4ff6-b8bd-23472bc632e4/Unknown.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_eb0f6d77-0887-4a47-858f-d49ce1e2b7c3/artifacts/gtejwrkd_images.jpeg"
                  alt="Black & White Comedy Productions"
                  className="w-full h-20 object-contain"
                />
              </div>
              
              {/* Duplicate set for seamless loop */}
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/9eac6892-84ea-4861-9538-4b88e9b7eb14/IMG_1068.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/4466ad3a-6f58-49dd-9917-1aee6231cb9e/IMG_1066.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/5c77a29c-924e-47e8-93d7-80cdba71bb80/IMG_1063.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/73809047-f1f5-4aec-b6d4-f29045a3bc16/IMG_1065.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/c4c02014-5957-4314-97b7-6b5a57cc61d7/IMG_1064.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/f17b089a-a2b5-4898-9b97-9f86f3759162/IMG_1067.jpg"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/0dc06ff2-f8be-497d-91ac-ebcee7239241/dc-magic-festival-2024-png.jpg"
                  alt="DC Magic Festival 2024"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://files.elfsightcdn.com/eafe4a4d-3436-495d-b748-5bdce62d911d/768acfd1-944d-4ff6-b8bd-23472bc632e4/Unknown.png"
                  alt="Client Logo"
                  className="w-full h-20 object-contain"
                />
              </div>
              <div className="logo-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_eb0f6d77-0887-4a47-858f-d49ce1e2b7c3/artifacts/gtejwrkd_images.jpeg"
                  alt="Black & White Comedy Productions"
                  className="w-full h-20 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section id="media" className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Experience the Show</h2>
            <div className="section-divider"></div>
            <p className="text-base md:text-lg text-gray-400 mt-4 italic">Audience participation strongly encouraged</p>
          </div>
          
          {/* Slider Gallery */}
          <div className="relative max-w-5xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
              {galleryImages.map((img, i) => (
                <div
                  key={i}
                  className={`absolute inset-0 transition-opacity duration-700 ${i === galleryIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            {/* Prev/Next Arrows */}
            <button
              onClick={() => setGalleryIndex((galleryIndex - 1 + galleryImages.length) % galleryImages.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white/80 hover:bg-magenta/70 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              data-testid="gallery-prev"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            </button>
            <button
              onClick={() => setGalleryIndex((galleryIndex + 1) % galleryImages.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white/80 hover:bg-magenta/70 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              data-testid="gallery-next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </button>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {galleryImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setGalleryIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === galleryIndex ? 'bg-magenta w-6' : 'bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom Text */}
          <div className="max-w-4xl mx-auto text-center py-16">
            <p className="text-xl md:text-2xl leading-relaxed text-gray-300 italic">
              The best part of the show isn't the mind-reading — it's watching the audience slowly realize they have no idea what's happening.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="relative z-10 overflow-hidden">
        {/* Heading sits above the video, on the page gradient */}
        <div className="relative z-20 pt-16 pb-12 md:pb-10 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="section-heading mb-4">What People Say</h2>
            <div className="section-divider"></div>
            <p className="text-base md:text-lg text-gray-400 mt-4 italic">Yes these are real reactions</p>
          </div>
        </div>
        {/* Video block with testimonials over it */}
        <div className="relative">
          {/* Video — natural 16:9 height on mobile so reviews fit over it; absolutely fills section on desktop */}
          <div className="relative w-full md:absolute md:inset-0 md:z-0">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="block w-full h-auto md:absolute md:inset-0 md:w-full md:h-full md:object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/pj2elgar_ryan_daniels_hero_loop_enhanced.mp4" type="video/mp4" />
            </video>
            {/* Vertical gradient overlay – darkens center where video sits, fades to transparent at top/bottom so it blends with page gradient */}
            <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-transparent via-black/80 to-transparent"></div>
            {/* Mobile swipe carousel sits inside the video bounds */}
            <div className="absolute inset-0 z-20 flex items-center md:hidden">
              <div className="w-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                {testimonials.map((testimonial, index) =>
                <div key={index} className="snap-center shrink-0 w-full text-center px-6">
                    <div className="flex justify-center gap-1 mb-1.5">
                      {[...Array(5)].map((_, i) =>
                      <Star key={i} className="w-3.5 h-3.5 text-magenta" fill="currentColor" />
                      )}
                    </div>
                    <p className="text-[12px] leading-snug mb-1.5 italic text-gray-200">
                      "{testimonial.text}"
                    </p>
                    <p className="font-semibold text-magenta text-[12px] leading-tight">{testimonial.author}</p>
                    <p className="text-[10px] text-gray-400 leading-tight">{testimonial.event}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Desktop testimonials grid */}
          <div className="hidden md:flex relative z-20 px-6 items-center" style={{ minHeight: 'min(56.25vw, 720px)' }}>
            <div className="max-w-6xl mx-auto w-full">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-6">
                {testimonials.map((testimonial, index) =>
                <div key={index} className="text-center px-2">
                    <div className="flex justify-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) =>
                      <Star key={i} className="w-4 h-4 text-magenta" fill="currentColor" />
                      )}
                    </div>
                    <p className="text-sm md:text-base mb-3 italic leading-relaxed text-gray-200">
                      "{testimonial.text}"
                    </p>
                    <p className="font-semibold text-magenta text-sm">{testimonial.author}</p>
                    <p className="text-xs text-gray-400">{testimonial.event}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">About Ryan</h2>
            <div className="section-divider"></div>
            <p className="text-base md:text-lg text-gray-400 mt-4 italic">Who is this guy?</p>
          </div>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <p className="body-large mb-6 leading-relaxed">
                Ryan Daniels performs interactive comedy mind-reading for people who think they've already seen everything.
              </p>
              <p className="body-large mb-6 leading-relaxed">
                Combining sharp humor with impossible audience interaction, his show turns ordinary moments into the kind of stories people bring up for years afterward. No cheesy magic. No awkward volunteers. Just a room full of people laughing, reacting, and wondering how any of this is possible.
              </p>
              <div className="mt-8">
                <button onClick={() => scrollToSection('contact')} className="cta-button">Book Ryan</button>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-magenta to-pink-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity duration-150"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_eb0f6d77-0887-4a47-858f-d49ce1e2b7c3/artifacts/wwnkzse6_Hand%20Reach%20Corporate.jpeg"
                  alt="Ryan Daniels Mentalism"
                  className="relative rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Shows Section - Performance Dates */}
      <section id="shows" className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">Tour</h2>
            <div className="section-divider"></div>
            <p className="text-base md:text-lg text-gray-400 mt-4 italic">Catch the show live</p>
          </div>
          
          <div className="space-y-8 max-w-3xl mx-auto">
            {/* Upcoming Events Section */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-center mb-8 text-magenta uppercase tracking-wide">Upcoming Events</h3>
              <p className="text-center text-gray-400 italic mb-4">More shows coming soon — stay tuned!</p>
            </div>

            {/* Previous Events Section */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-center mb-8 text-gray-400 uppercase tracking-wide">Previous Events</h3>

              {/* July 18, 2026 - Mimslyn Inn */}
              <div className="bg-neutral-mid/50 border border-white/5 rounded-2xl p-6 mb-4 opacity-75">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-500">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-300">July 18, 2026</p>
                      <p className="text-magenta font-medium">Dinner & Magic Show</p>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        Mimslyn Inn, Luray, VA
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide" style={{ backgroundColor: 'rgba(255, 31, 143, 0.2)', color: 'var(--color-magenta)' }}>Sold Out</span>
                </div>
              </div>

              {/* May 15, 2026 - Magic Night DC */}
              <div className="bg-neutral-mid/50 border border-white/5 rounded-2xl p-6 mb-4 opacity-75">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-500">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-300">May 15, 2026</p>
                      <p className="text-magenta font-medium">Magic Night DC</p>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        BluntSluts DC, Washington, DC
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide" style={{ backgroundColor: 'rgba(255, 31, 143, 0.2)', color: 'var(--color-magenta)' }}>Sold Out</span>
                </div>
              </div>

              {/* Sept 6, 2025 - Smokies Cigar Lounge */}
              <div className="bg-neutral-mid/50 border border-white/5 rounded-2xl p-6 mb-4 opacity-75">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-500">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-300">Sept 6, 2025 • 7 PM</p>
                      <p className="text-magenta font-medium">Weekend Music & Comedy</p>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        Smokies Cigar Lounge, Charlottesville, VA
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide" style={{ backgroundColor: 'rgba(255, 31, 143, 0.2)', color: 'var(--color-magenta)' }}>Sold Out</span>
                </div>
              </div>

              {/* Aug 30, 2025 - Magic Mic */}
              <div className="bg-neutral-mid/50 border border-white/5 rounded-2xl p-6 mb-4 opacity-75">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-500">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-300">Aug 30, 2025 • 9 PM</p>
                      <p className="text-magenta font-medium">Magic Mic - DC Festival of Magic</p>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        Capital Hilton, Washington, DC
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide" style={{ backgroundColor: 'rgba(255, 31, 143, 0.2)', color: 'var(--color-magenta)' }}>Sold Out</span>
                </div>
              </div>

              {/* July 18, 2025 - Hog Shack */}
              <div className="bg-neutral-mid/50 border border-white/5 rounded-2xl p-6 mb-4 opacity-75">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="text-gray-500">
                      <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM9 14H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm-8 4H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-gray-300">July 18, 2025 • 8 PM</p>
                      <p className="text-magenta font-medium">Black and White Comedy Productions</p>
                      <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                        Hog Shack Bar-B-Q, Manassas, VA
                      </p>
                    </div>
                  </div>
                  <span className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide" style={{ backgroundColor: 'rgba(255, 31, 143, 0.2)', color: 'var(--color-magenta)' }}>Sold Out</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup Section - Widget */}
      <section className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="relative z-10" id="rd-newsletter-widget" ref={newsletterRef}></div>
      </section>


      {/* Ghosted Show Section */}
      <section id="ghosted" className="py-20 px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-3 py-1 bg-magenta/10 border border-magenta/30 rounded-full mb-4">
                <span className="text-magenta text-xs font-medium tracking-wide uppercase">Featured Show</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Ghosted</h2>
              <p className="body-large mb-6 text-gray-300">
                Interested in seeing a more unique show? Want to experience the perfect date night event?
              </p>
              <a
                href="https://ghostedshow.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button inline-block">

                Learn About Ghosted
              </a>
            </div>
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-magenta to-pink-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
              <img
                src="https://images.squarespace-cdn.com/content/v1/67d75cecf3ca965be8cd1ed6/c55f1084-d441-4157-ac18-2f9ebd9742cc/IMG_0577.jpeg"
                alt="Ghosted Show"
                className="relative rounded-2xl shadow-2xl w-full" />

            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative z-10 overflow-hidden">
        <div className="max-w-2xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Let's Work Together</h2>
            <div className="section-divider"></div>
            <p className="text-base md:text-lg text-gray-400 mt-4 italic">No crystal balls. Probably</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {formSubmitted ? (
              <div className="text-center py-12 space-y-4" data-testid="contact-thank-you">
                <div className="w-16 h-16 mx-auto rounded-full bg-magenta/20 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-magenta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Thank You!</h3>
                <p className="text-gray-400 max-w-md mx-auto">Your message has been sent successfully. I'll get back to you as soon as possible.</p>
                <button
                  type="button"
                  onClick={() => setFormSubmitted(false)}
                  className="mt-6 px-6 py-2 border border-magenta/50 text-magenta rounded-full hover:bg-magenta/10 transition-colors text-sm"
                  data-testid="send-another-message-btn"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block body-small mb-2 text-gray-400">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="form-input" />

              </div>
              <div>
                <label htmlFor="lastName" className="block body-small mb-2 text-gray-400">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="form-input" />

              </div>
            </div>
            <div>
              <label htmlFor="email" className="block body-small mb-2 text-gray-400">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-input" />

            </div>
            <div>
              <label htmlFor="subject" className="block body-small mb-2 text-gray-400">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="form-input" />

            </div>
            <div>
              <label htmlFor="message" className="block body-small mb-2 text-gray-400">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="form-input">
              </textarea>
            </div>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="cta-button large w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
              </>
            )}
          </form>
          <div className="mt-12 text-center">
            <p className="body-medium mb-4 text-gray-400">Or reach out directly:</p>
            <a href="mailto:ryan@ryandanielsmentalist.com" className="inline-flex items-center gap-2 text-magenta hover:text-white transition-colors duration-300 text-lg font-medium">
              <Mail size={20} />
              ryan@ryandanielsmentalist.com
            </a>
          </div>
        </div>
      </section>

      {/* Showreel Modal */}
      {showreelOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => { setShowreelOpen(false); setVideoPlaying(false); }}
          data-testid="showreel-modal"
        >
          <div
            className="relative w-full max-w-4xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setShowreelOpen(false); setVideoPlaying(false); }}
              className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
              data-testid="showreel-close-btn"
            >
              <X size={28} />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-neutral-900">
              <video
                autoPlay
                controls
                playsInline
                className="w-full h-full"
                data-testid="showreel-video"
              >
                <source src={`${process.env.REACT_APP_BACKEND_URL}/api/static/promo.mp4`} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-3 pb-20 md:pb-6 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-xl md:text-2xl font-semibold text-white mb-8">Let's Make Your Event Impossible To Forget</p>
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 pb-6 md:pb-0">
            {/* Logo - Left on desktop, centered on mobile */}
            <div className="flex justify-center md:justify-start">
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="cursor-pointer"
                aria-label="Return to top"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_2307e085-4eaf-4338-88bf-6170046d3d85/artifacts/mjuj40fa_Ryan%20daniels%20mentalist-4.png"
                  alt="Ryan Daniels Mentalist"
                  style={{ height: '120px' }}
                />
              </button>
            </div>
            
            {/* Social Media Links - Center */}
            <div className="flex items-center justify-center gap-6">
              <a 
                href="https://www.facebook.com/ryandanielsmentalist"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-300"
                aria-label="Facebook"
                style={{ color: 'var(--color-magenta)' }}
              >
                <Facebook size={28} />
              </a>
              <a 
                href="https://www.instagram.com/mentalistryand?igsh=MTlxZnJhZ3AzZjl5dA%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-300"
                aria-label="Instagram"
                style={{ color: 'var(--color-magenta)' }}
              >
                <Instagram size={28} />
              </a>
              <a 
                href="https://youtube.com/@ryandanielsmagic?si=XvAmTYceoInryCyg"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-300"
                aria-label="YouTube"
                style={{ color: 'var(--color-magenta)' }}
              >
                <Youtube size={28} />
              </a>
              <a 
                href="https://www.tiktok.com/@ryandmagic?_r=1&_t=ZT-938A3IBjVaI"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-300"
                aria-label="TikTok"
                style={{ color: 'var(--color-magenta)' }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
            
          </div>
          <div className="flex justify-center mt-4">
            <p className="body-small text-gray-500">&copy; 2026 Ryan Daniels. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;